import React, { useState, useEffect } from 'react';
import '../styles/ContractSettings.css';
import { getContractSettings, updateContractSettings } from '../utils/api';
import Dialog from '../components/Dialog';
import HelpTip from '../components/HelpTip';

const ContractSettings = () => {
  // 초기 상태값 설정
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hourlyWage, setHourlyWage] = useState('12,000');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('18:00');
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // 고급 설정 초기값
  const [autoLeaveTime, setAutoLeaveTime] = useState('20:00');
  const [arrivalStartTime, setArrivalStartTime] = useState('08:50');
  const [arrivalEndTime, setArrivalEndTime] = useState('09:10');
  
  // 여러 개의 휴게 시간 관리를 위한 배열
  const [breakTimes, setBreakTimes] = useState([
    { id: 1, start: '12:00', end: '13:00', enabled: true },
    { id: 2, start: '15:00', end: '16:00', enabled: true },
    { id: 3, start: '17:00', end: '18:00', enabled: true }
  ]);
  
  // 토글 상태 관리
  const [settings, setSettings] = useState({
    hourlyWage: true,
    workTime: true,
    autoLeave: true,
    arrivalRange: true,
    breakTime: true
  });
  
  // 다이얼로그 관련 상태
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');

  // 도움말 상태 관리
  const [helpTips, setHelpTips] = useState({
    workTime: false,
    autoLeave: false,
    arrivalRange: false,
    breakTime: false
  });

  // 도움말 내용 정의
  const helpContents = {
    workTime: {
      title: "정규 출퇴근 시간",
      content: "근로자의 기본 출근 시간과 퇴근 시간을 설정합니다. 이 시간을 기준으로 (정상 출근, 지각, 조퇴) 여부가 판단됩니다."
    },
    autoLeave: {
      title: "자동 퇴근 시간",
      content: "퇴근 버튼을 누르지 않아도, (설정된 시간 이후 자동으로 퇴근 처리)됩니다."
    },
    arrivalRange: {
      title: "출근 인정 범위 시간",
      content: "정규 출근 시간을 기준으로, (정상 출근으로 인정되는 시간 범위)를 설정합니다. 예: 정규 출근 시간 09:00, 인정 범위 08:50~09:10으로 설정 시 → 08:50부터 09:10 사이 출근은 (정상 출근)으로 처리됩니다."
    },
    breakTime: {
      title: "휴게 시간 설정",
      content: "근무 중 휴게 시간으로 설정된 구간은 (총 근무 시간에서 자동으로 제외)됩니다. 여러 개의 구간 설정이 가능하며, 해당 구간의 총합이 제외됩니다."
    }
  };

  // 페이지 로드 시 API에서 계약 정보 가져오기
  useEffect(() => {
    const fetchContractSettings = async () => {
      try {
        setLoading(true);
        
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const companyCode = userInfo.middleCompanyCode || userInfo.businessNumber;
        
        if (!companyCode) {
          throw new Error('회사 코드를 찾을 수 없습니다.');
        }
        
        const response = await getContractSettings(companyCode);
        
        if (response.status === 'success' && response.data) {
          const data = response.data;
          
          // 기본 설정
          setHourlyWage(data.wage?.toLocaleString() || '12,000');
          setStartTime(data.check_in_time?.substring(0, 5) || '09:00');
          setEndTime(data.check_out_time?.substring(0, 5) || '18:00');
          
          // 고급 설정
          setAutoLeaveTime(data.auto_check_out_time?.substring(0, 5) || '20:00');
          
          // 출근 인정 범위 시간 - 새로운 구조 사용
          setArrivalStartTime(data.check_in_grace_start_time?.substring(0, 5) || '08:50');
          setArrivalEndTime(data.check_in_grace_end_time?.substring(0, 5) || '09:10');
          
          // 휴게 시간
          if (data.rest_times && Array.isArray(data.rest_times)) {
            setBreakTimes(
              data.rest_times.map((bt, index) => ({
                id: index + 1,
                start: bt.start_time.substring(0, 5),
                end: bt.end_time.substring(0, 5),
                enabled: bt.is_active
              }))
            );
          }
          
          // 토글 설정
          setSettings({
            hourlyWage: data.is_wage_active === true,
            workTime: data.is_check_in_time_active === true,
            autoLeave: data.is_auto_check_out_time_active === true,
            arrivalRange: data.is_grace_period_active === true,
            breakTime: data.rest_times?.[0]?.is_active === true
          });
        }
      } catch (err) {
        console.error('계약 정보 로드 오류:', err);
        setError(err.message || '계약 정보를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchContractSettings();
  }, []);

  // 수정 모드 전환 및 저장
  const toggleEditMode = async () => {
    if (isEditing) {
      try {
        // 모든 시간 필드 유효성 검사
        const timeFields = [startTime, endTime, autoLeaveTime, arrivalStartTime, arrivalEndTime];
        const invalidFields = timeFields.filter(time => !isValidTimeFormat(time));
        
        // 휴게 시간 유효성 검사
        const invalidBreakTimes = breakTimes.some(bt => 
          !isValidTimeFormat(bt.start) || !isValidTimeFormat(bt.end)
        );
        
        if (invalidFields.length > 0 || invalidBreakTimes) {
          setDialogType(Dialog.TYPES.TIME_FORMAT_ERROR);
          setDialogOpen(true);
          return;
        }
        
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const companyCode = userInfo.middleCompanyCode || userInfo.businessNumber;
        
        // API 데이터 형식에 맞게 변환
        const updateData = {
          wage: parseInt(hourlyWage.replace(/,/g, '')) || 0,
          is_wage_active: settings.hourlyWage,
          check_in_time: `${startTime}:00`,
          check_out_time: `${endTime}:00`,
          is_check_in_time_active: settings.workTime,
          is_check_out_time_active: settings.workTime,
          auto_check_out_time: `${autoLeaveTime}:00`,
          is_auto_check_out_time_active: settings.autoLeave,
          
          check_in_grace_start_time: `${arrivalStartTime}:00`,
          check_in_grace_end_time: `${arrivalEndTime}:00`,
          is_grace_period_active: settings.arrivalRange,
          
          rest_times: breakTimes.map(bt => ({
            start_time: `${bt.start}:00`,
            end_time: `${bt.end}:00`,
            is_active: bt.enabled
          }))
        };
        
        // API 호출하고 응답 확인
        const response = await updateContractSettings(companyCode, updateData);
        
        // 서버 응답 확인
        if (response.status === 'success') {
          // 성공 다이얼로그 표시
          setDialogType(Dialog.TYPES.CONTRACT_EDIT_SUCCESS);
          setDialogMessage('계약 정보가 성공적으로 업데이트되었습니다.');
          setDialogOpen(true);
          setIsEditing(false); // 성공 시 수정 모드 종료
        } else {
          // 응답은 왔지만 상태가 success가 아닌 경우
          throw new Error(response.message || '계약 정보 업데이트에 실패했습니다.');
        }
      } catch (err) {
        console.error('계약 정보 업데이트 오류:', err);
        // 에러 메시지 다이얼로그로 표시
        setDialogType(Dialog.TYPES.ERROR); // Dialog.jsx에 ERROR 타입 필요
        setDialogMessage(err.message || '계약 정보 업데이트에 실패했습니다.');
        setDialogOpen(true);
        return; // 실패 시 수정 모드 유지
      }
    } else {
      setIsEditing(true); // 수정 모드 시작
    }
  };

  // 다이얼로그 닫기 핸들러
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // 다이얼로그 확인 핸들러
  const handleConfirmDialog = () => {
    setDialogOpen(false);
  };

  // 토글 핸들러
  const toggleSetting = (setting) => {
    setSettings({
      ...settings,
      [setting]: !settings[setting]
    });
  };
  
  // 휴게 시간 추가 함수
  const addBreakTime = () => {
    const newId = Math.max(...breakTimes.map(item => item.id), 0) + 1;
    setBreakTimes([
      ...breakTimes,
      { id: newId, start: '12:00', end: '13:00', enabled: true }
    ]);
  };
  
  // 휴게 시간 삭제 함수
  const removeBreakTime = (id) => {
    setBreakTimes(breakTimes.filter(item => item.id !== id));
  };

  // 고급 설정 토글
  const toggleAdvancedSettings = () => {
    setShowAdvanced(!showAdvanced);
  };

  // toggleBreakTime 함수 추가
  const toggleBreakTime = (id) => {
    setBreakTimes(breakTimes.map(item => 
      item.id === id ? { ...item, enabled: !item.enabled } : item
    ));
  };

  // 토글 스위치 컴포넌트
  const ToggleSwitch = ({ enabled, onToggle }) => (
    <div className={`toggle-switch ${enabled ? 'enabled' : 'disabled'}`} onClick={onToggle}>
      <div className="toggle-button"></div>
    </div>
  );

  // 시간 포맷 유효성 검사 함수
  const isValidTimeFormat = (time) => {
    // HH:MM 형식인지 확인 (00:00 ~ 23:59)
    const regex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
    return regex.test(time);
  };

  // 시간 입력 핸들러 (포맷팅 + 유효성 검사)
  const handleTimeChange = (value, setter, prevValue) => {
    // 숫자와 콜론만 입력 가능하도록
    const cleaned = value.replace(/[^\d:]/g, '');
    
    // 입력 중인 경우는 그대로 허용 (예: "1" 또는 "12:" 등)
    if (cleaned.length <= 5 && (cleaned.match(/:/g) || []).length <= 1) {
      setter(cleaned);
      return;
    }
    
    // 완성된 형태일 때 유효성 검사
    if (isValidTimeFormat(cleaned)) {
      setter(cleaned);
    } else {
      // 유효하지 않은 형식인 경우 다이얼로그 표시
      setDialogType(Dialog.TYPES.TIME_FORMAT_ERROR);
      setDialogOpen(true);
      setter(prevValue); // 이전 값으로 복원
    }
  };

  // 임금 입력 핸들러 (숫자 포맷팅)
  const handleWageChange = (value) => {
    // 숫자 또는 콤마만 허용
    if (!/^[\d,]*$/.test(value)) {
      setDialogType(Dialog.TYPES.WAGE_FORMAT_ERROR);
      setDialogOpen(true);
      return;
    }
    
    // 숫자만 추출 (콤마 제거)
    const numericValue = value.replace(/[^\d]/g, '');
    
    // 숫자가 없으면 빈 값
    if (!numericValue) {
      setHourlyWage('');
      return;
    }
    
    // 천단위 콤마 추가
    const formattedValue = parseInt(numericValue).toLocaleString();
    setHourlyWage(formattedValue);
  };

  // 시간 포맷터 (저장 전 검증)
  const formatTimeForSave = (time) => {
    if (!isValidTimeFormat(time)) {
      return null; // 유효하지 않은 시간은 null 반환
    }
    return time;
  };

  // 도움말 토글 함수
  const toggleHelpTip = (tip) => {
    setHelpTips({
      workTime: false,
      autoLeave: false,
      arrivalRange: false,
      breakTime: false,
      [tip]: !helpTips[tip]
    });
  };

  // 도움말 닫기 함수 (외부 클릭시)
  const closeAllHelpTips = () => {
    setHelpTips({
      workTime: false,
      autoLeave: false,
      arrivalRange: false,
      breakTime: false
    });
  };

  // 전체 클릭 이벤트 리스너 추가
  useEffect(() => {
    document.addEventListener('click', closeAllHelpTips);
    return () => {
      document.removeEventListener('click', closeAllHelpTips);
    };
  }, []);

  if (loading) return <div className="loading">정보를 불러오는 중...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="contract-settings-container">
      <div className="contract-settings-header">
        <h1 className="contract-title">계약 정보 설정</h1>
        <button 
          className={`edit-button ${isEditing ? 'save-mode' : ''}`} 
          onClick={toggleEditMode}
        >
          {isEditing ? (
            <>
              <span className="ok-icon">OK</span>
              수정완료
            </>
          ) : (
            <>
              <span className="edit-icon">✎</span>
              수정하기
            </>
          )}
        </button>
      </div>

      <div className="contract-section">
        <div className="section-title">기본 임금 설정</div>
        <div className="setting-row">
          <div className={`wage-input-container ${settings.hourlyWage ? 'active' : 'inactive'}`}>
            {isEditing ? (
              <input 
                type="text" 
                className="wage-input"
                style={{ 
                  backgroundColor: 'transparent',
                  border: 'none',
                  width: '100%'
                }} 
                value={hourlyWage} 
                onChange={(e) => handleWageChange(e.target.value)}
              />
            ) : (
              <div className="wage-display">{hourlyWage}</div>
            )}
            <span className="wage-unit">원</span>
          </div>
          {isEditing && (
            <ToggleSwitch 
              enabled={settings.hourlyWage} 
              onToggle={() => toggleSetting('hourlyWage')} 
            />
          )}
        </div>
      </div>

      <div className="contract-section">
        <div className="section-title">
          <span>정규 출퇴근 시간</span>
          <HelpTip 
            tipId="workTime"
            title={helpContents.workTime.title}
            content={helpContents.workTime.content}
          />
        </div>
        <div className="setting-row">
          <div className={`time-input-container regular-work-time ${settings.workTime ? 'active' : 'inactive'}`}>
            {isEditing ? (
              <>
                <input 
                  type="text" 
                  className="time-input"
                  style={{ 
                    backgroundColor: 'transparent',
                    border: 'none'
                  }} 
                  value={startTime} 
                  onChange={(e) => handleTimeChange(e.target.value, setStartTime, startTime)}
                  placeholder="00:00"
                />
                <span className="time-separator">-</span>
                <input 
                  type="text" 
                  className="time-input"
                  style={{ 
                    backgroundColor: 'transparent',
                    border: 'none'
                  }} 
                  value={endTime} 
                  onChange={(e) => handleTimeChange(e.target.value, setEndTime, endTime)}
                  placeholder="00:00"
                />
              </>
            ) : (
              <>
                <span>{startTime}</span>
                <span className="time-separator">-</span>
                <span>{endTime}</span>
              </>
            )}
          </div>
          {isEditing && (
            <ToggleSwitch 
              enabled={settings.workTime} 
              onToggle={() => toggleSetting('workTime')} 
            />
          )}
        </div>
      </div>

      <div className="contract-section advanced-settings-section">
        <button className="advanced-button" onClick={toggleAdvancedSettings}>
          <span className="arrow-icon">
            {showAdvanced ? 'v' : '>'}
          </span>
          고급 설정
        </button>

        {showAdvanced && (
          <div className="advanced-options">
            <div className="advanced-option">
              <div className="section-title">
                <span>자동 퇴근 시간</span>
                <HelpTip 
                  tipId="autoLeave"
                  title={helpContents.autoLeave.title}
                  content={helpContents.autoLeave.content}
                />
              </div>
              <div className="setting-row">
                <div className={`time-input-container advanced-time-container ${settings.autoLeave ? 'active' : 'inactive'}`}>
                  {isEditing ? (
                    <input 
                      type="text" 
                      className="time-input"
                      style={{ 
                        backgroundColor: 'transparent',
                        border: 'none',
                        width: '100%',
                        textAlign: 'center'
                      }} 
                      value={autoLeaveTime} 
                      onChange={(e) => handleTimeChange(e.target.value, setAutoLeaveTime, autoLeaveTime)}
                    />
                  ) : (
                    <span>{autoLeaveTime}</span>
                  )}
                </div>
                {isEditing && (
                  <ToggleSwitch 
                    enabled={settings.autoLeave} 
                    onToggle={() => toggleSetting('autoLeave')} 
                  />
                )}
              </div>
            </div>

            <div className="advanced-option">
              <div className="section-title">
                <span>출근 인정 범위 시간</span>
                <HelpTip 
                  tipId="arrivalRange"
                  title={helpContents.arrivalRange.title}
                  content={helpContents.arrivalRange.content}
                />
              </div>
              <div className="setting-row">
                <div className={`time-input-container advanced-time-container ${settings.arrivalRange ? 'active' : 'inactive'}`}>
                  {isEditing ? (
                    <>
                      <input 
                        type="text" 
                        className="time-input"
                        style={{ 
                          backgroundColor: 'transparent',
                          border: 'none'
                        }} 
                        value={arrivalStartTime} 
                        onChange={(e) => handleTimeChange(e.target.value, setArrivalStartTime, arrivalStartTime)}
                      />
                      <span className="time-separator">-</span>
                      <input 
                        type="text" 
                        className="time-input"
                        style={{ 
                          backgroundColor: 'transparent',
                          border: 'none'
                        }} 
                        value={arrivalEndTime} 
                        onChange={(e) => handleTimeChange(e.target.value, setArrivalEndTime, arrivalEndTime)}
                      />
                    </>
                  ) : (
                    <>
                      <span>{arrivalStartTime}</span>
                      <span className="time-separator">-</span>
                      <span>{arrivalEndTime}</span>
                    </>
                  )}
                </div>
                {isEditing && (
                  <ToggleSwitch 
                    enabled={settings.arrivalRange} 
                    onToggle={() => toggleSetting('arrivalRange')} 
                  />
                )}
              </div>
            </div>

            <div className="advanced-option">
              <div className="section-title">
                <span>휴게 시간 설정</span>
                <HelpTip 
                  tipId="breakTime"
                  title={helpContents.breakTime.title}
                  content={helpContents.breakTime.content}
                />
              </div>
              
              <div className="break-times-list">
                {breakTimes.map((breakTime, index) => (
                  <div className="break-time-row" key={breakTime.id}>
                    <div className={`time-input-container advanced-time-container ${breakTime.enabled ? 'active' : 'inactive'}`}>
                      {isEditing ? (
                        <>
                          <input 
                            type="text" 
                            className="time-input"
                            style={{ 
                              backgroundColor: 'transparent',
                              border: 'none'
                            }} 
                            value={breakTime.start} 
                            onChange={(e) => {
                              const newBreakTimes = [...breakTimes];
                              newBreakTimes[index].start = e.target.value;
                              setBreakTimes(newBreakTimes);
                            }}
                          />
                          <span className="time-separator">-</span>
                          <input 
                            type="text" 
                            className="time-input"
                            style={{ 
                              backgroundColor: 'transparent',
                              border: 'none'
                            }} 
                            value={breakTime.end} 
                            onChange={(e) => {
                              const newBreakTimes = [...breakTimes];
                              newBreakTimes[index].end = e.target.value;
                              setBreakTimes(newBreakTimes);
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <span>{breakTime.start}</span>
                          <span className="time-separator">-</span>
                          <span>{breakTime.end}</span>
                        </>
                      )}
                    </div>
                    
                    {index > 0 && isEditing && (
                      <button 
                        className="remove-break-time-btn" 
                        onClick={() => removeBreakTime(breakTime.id)}
                      >
                        -
                      </button>
                    )}
                    {isEditing && (
                      <ToggleSwitch 
                        enabled={breakTime.enabled} 
                        onToggle={() => toggleBreakTime(breakTime.id)} 
                      />
                    )}
                  </div>
                ))}
              </div>
              
              {isEditing && (
                <button 
                  className="add-break-time-btn" 
                  onClick={addBreakTime}
                >
                  +
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 다이얼로그 컴포넌트 */}
      <Dialog
        isOpen={dialogOpen}
        type={dialogType}
        message={dialogMessage}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDialog}
      />
    </div>
  );
};

export default ContractSettings; 